import { useState } from "react";
import { Box, Typography, IconButton, Sheet, Tooltip } from "@mui/joy";
import moment from "moment";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import "./index.scss";
import { Autocomplete, Button, Textarea } from "../../shared";
import { Plus } from "lucide-react";
import { FormProvider, useForm } from "react-hook-form";
import { useTask } from "../../hooks/query";
import { useSubTask } from "../../hooks";

// Types
type TaskStatus = "NOT-STARTED" | "IN-PROGRESS" | "IN-REVIEW" | "COMPLETED";

interface Task {
    Id: string;
    ProjectId: string;
    ParentTaskId: string | null;
    Name: string;
    Description: string;
    Status: TaskStatus;
    SortOrder: number;
    CreatedAt: string;
    UpdatedAt: string;
    ExecutionDate: string;
}

const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

interface CalendarProps {
    activeTask: any;
}

const Calendar = ({ activeTask }: CalendarProps) => {
    const { tasks } = useSubTask();
    const [currentMonth, setCurrentMonth] = useState(moment());

    const startDay = currentMonth.clone().startOf("month").startOf("week");
    const endDay = currentMonth.clone().endOf("month").endOf("week");

    const day = startDay.clone().subtract(1, "day");
    const calendar = [];
    while (day.isBefore(endDay, "day")) {
        calendar.push(
            Array(7)
                .fill(0)
                .map(() => day.add(1, "day").clone())
        );
    }

    return (
        <Box className={"CalendarWrapper"}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography level="title-md">{currentMonth.format("MMMM YYYY")}</Typography>
                <Box display={"flex"} gap={1}>
                    <IconButton
                        size="sm"
                        onClick={() => setCurrentMonth((prev) => prev.clone().subtract(1, "month"))}
                        variant="soft"
                        sx={{ border: "1px solid", borderColor: "divider" }}
                    >
                        <ChevronLeft />
                    </IconButton>

                    <IconButton
                        size="sm"
                        onClick={() => setCurrentMonth((prev) => prev.clone().add(1, "month"))}
                        variant="soft"
                        sx={{ border: "1px solid", borderColor: "divider" }}
                    >
                        <ChevronRight />
                    </IconButton>
                </Box>
            </Box>

            <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" textAlign="center" mb={1}>
                {daysOfWeek.map((day, idx) => (
                    <Typography
                        key={day}
                        level="body-sm"
                        color={idx === 0 || idx === 6 ? "danger" : undefined}
                        fontWeight="lg"
                    >
                        {day}
                    </Typography>
                ))}
            </Box>

            <Box display="grid" gridTemplateRows={`repeat(${calendar.length}, 1fr)`} gap={0}>
                {calendar.map((week, weekIdx) => (
                    <Box key={weekIdx} display="grid" gridTemplateColumns="repeat(7, 1fr)" minHeight={100}>
                        {week.map((date, idx) => (
                            <DateComponent
                                date={date}
                                key={idx}
                                idx={idx}
                                currentMonth={currentMonth}
                                tasks={tasks || []}
                                activeTask={activeTask}
                            />
                        ))}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

interface IDateComponent {
    date: any;
    idx: number;
    currentMonth: any;
    tasks: Task[];
    activeTask: any;
}

function DateComponent(props: IDateComponent) {
    const { date, idx, currentMonth, tasks, activeTask } = { ...props };

    const [openTaskCreate, setOpenTaskCreate] = useState(false);

    const isToday = date.isSame(moment(), "day");
    const isSameDay = (a: moment.Moment, b: moment.Moment) => a.isSame(b, "day");
    const isSameMonth = (a: moment.Moment, b: moment.Moment) => a.isSame(b, "month");

    const getTasksForDay = (date: moment.Moment) => {
        return tasks.filter((task) => isSameDay(moment(task.ExecutionDate), date));
    };

    return (
        <Sheet
            variant="outlined"
            className={"DateWrapper"}
            sx={{
                p: 1,
                position: "relative",
                minHeight: 100,
                //border: "1px solid #e0e0e0",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                alignItems: "flex-start",
            }}
        >
            <Tooltip
                title={
                    <NewTask
                        activeTask={activeTask}
                        date={date}
                        onTaskCreationComplete={() => setOpenTaskCreate(false)}
                    />
                }
                variant="outlined"
                arrow
                placement="left-start"
                open={openTaskCreate}
            >
                <Box className={"new_task_icon"}>
                    <IconButton
                        size={"sm"}
                        variant={"solid"}
                        onClick={() => setOpenTaskCreate((current) => !current)}
                        sx={{
                            borderRadius: 6,
                            "--IconButton-size": "24px",
                        }}
                    >
                        <Plus size={16} />
                    </IconButton>
                </Box>
            </Tooltip>

            <Typography
                level="title-sm"
                fontWeight="lg"
                mb={0.5}
                textAlign={"left"}
                color={idx === 0 || idx === 6 ? "danger" : undefined}
                sx={(theme) =>
                    isToday
                        ? {
                              fontWeight: 500,
                              border: "1px solid",
                              borderColor: theme.palette.success.outlinedBorder,
                              background: theme.palette.success.softBg,
                              height: 25,
                              width: 25,
                              borderRadius: "50%",
                              opacity: 1,
                              textAlign: "center",
                          }
                        : {
                              fontWeight: 500,
                              opacity: isSameMonth(date, currentMonth) ? "1" : "0.5",
                          }
                }
            >
                {date.date()}
            </Typography>
            <Box display="flex" flexDirection="column" gap={0.5} width={"100%"}>
                {getTasksForDay(date).map((task) => (
                    <Box
                        key={task.Id}
                        sx={{
                            bgcolor: "#ccc",
                            color: "#000",
                            fontSize: 12,
                            borderRadius: 4,
                            px: 0.6,
                            width: "100%",
                            justifyContent: "flex-start",
                        }}
                    >
                        <Box>
                            <Typography level="body-xs" fontWeight="md">
                                {task.Name}
                            </Typography>
                            {/* <Typography level="body-xs" fontSize={10}>
                                                        {task.time}
                                                    </Typography> */}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Sheet>
    );
}

const defaultValues = {
    taskDescription: "",
    status: "NOT-STARTED",
};

const schema = z.object({
    taskDescription: z.string().min(5, "Description is required"),
    status: z.string(),
});

function NewTask({
    activeTask,
    date,
    onTaskCreationComplete,
}: {
    activeTask: any;
    date: any;
    onTaskCreationComplete: any;
}) {
    const { useCreate, useListSubtask } = useTask();
    const createTaskSvc = useCreate();
    const listSubTaskSvc = useListSubtask();
    const { setTasks } = useSubTask();

    const methods = useForm({
        mode: "onChange",
        defaultValues,
        resolver: zodResolver(schema),
    });
    const {
        handleSubmit,
        formState: { isValid },
    } = methods;

    const handleOnSubmit = async (event: any) => {
        try {
            const params = {
                ProjectId: activeTask.ProjectId,
                ParentTaskId: activeTask.Id,
                Name: event.taskDescription,
                Status: event.status,
                SortOrder: 0,
                Description: event.taskDescription,
                ExecutionDate: date.utc().format("YYYY-MM-DD HH:mm:ss"),
            };
            //console.log(activeTask, params);
            await createTaskSvc.mutateAsync(params);

            const listParams = {
                ProjectId: activeTask.ProjectId,
                TaskId: activeTask.Id,
            };
            const newTasks = await listSubTaskSvc.mutateAsync(listParams);
            setTasks(newTasks);
            onTaskCreationComplete();
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(handleOnSubmit)}>
                <Box sx={{ width: 300, maxWidth: "100%" }}>
                    <Box sx={{ width: "100%" }}>
                        <Textarea
                            sx={{
                                width: "100%",
                                "&::before": {
                                    display: "none",
                                },
                                "&:focus-within": {
                                    outline: "0px solid var(--Textarea-focusedHighlight)",
                                    outlineOffset: "2px",
                                },
                            }}
                            placeholder={"What needs to be done"}
                            variant={"plain"}
                            label={""}
                            name={"taskDescription"}
                            autoFocus
                        />
                        <Box sx={{ mb: 0.5, ml: 0.5, display: "flex", gap: 1, alignItems: "center" }}>
                            <Autocomplete
                                size="sm"
                                options={[
                                    { label: "To Do", value: "NOT-STARTED" },
                                    { label: "In Progress", value: "IN-PROGRESS" },
                                    { label: "In Review", value: "IN-REVIEW" },
                                    { label: "Completed", value: "COMPLETED" },
                                ]}
                                name="status"
                                label={""}
                            />
                            <Button
                                size={"sm"}
                                type={"submit"}
                                sx={{ mt: 0.5, flex: 1, mr: 1 }}
                                disabled={!isValid}
                                loading={createTaskSvc.isPending}
                                loadingPosition={"start"}
                            >
                                Create
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </form>
        </FormProvider>
    );
}
export default Calendar;
export { default as GlobalCalendar } from "./GlobalCalendar";
